import { createElement } from 'react';
import { Routes, Route, useLocation, useNavigate, useParams } from 'react-router-dom';
import { type ContextualNavSection } from '@vnetwork/ui/layout';
import { useSetContextualNav } from '@/components/layout/nav-context';
import {
  VMWARE_NAV_GROUPS,
  VMWARE_INSTANCE_NAV,
  vmwareHref,
  vmwareActiveId,
} from './nav';
import { VmwareDashboard } from './VmwareDashboard';
import { InstanceList } from './instances/InstanceList';
import { InstanceDetail } from './instances/InstanceDetail';
import { InstanceConsole } from './instances/InstanceConsole';

/**
 * Service-scoped VMware (Enterprise Compute / Bear) console. Mirrors
 * ComputeConsole's contextual-nav registration pattern — the sidebar swaps to
 * this service's scope while the user is inside it. VMware has no
 * Volumes/Snapshots/VPC/LB/SSH-Keys — only Dashboard + Instances with 4 tabs.
 */
export function VmwareConsole() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { serviceId } = useParams<{ serviceId: string }>();
  const base = `/services/compute/${serviceId}`;

  useSetContextualNav(() => {
    // Inside /instances/:instanceId/* — swap to the instance's sub-nav.
    const rel = pathname.slice(base.length).split('/').filter(Boolean);
    const instanceId = rel[0] === 'instances' && rel[1] ? rel[1] : null;

    if (instanceId) {
      const instanceBase = `${base}/instances/${instanceId}`;
      const instanceHref = (slug: string) => (slug ? `${instanceBase}/${slug}` : instanceBase);
      const sub = rel[2] ?? '';
      const known = VMWARE_INSTANCE_NAV.some((n) => n.slug === sub);
      return {
        sections: [
          {
            heading: 'Instance',
            items: VMWARE_INSTANCE_NAV.map((entry) => ({
              id: instanceHref(entry.slug),
              label: entry.label,
              icon: createElement(entry.icon, { size: 16 }),
              onSelect: () => navigate(instanceHref(entry.slug)),
            })),
          },
        ],
        activeId: known && sub ? instanceHref(sub) : instanceBase,
        back: { label: 'Back to instances', onSelect: () => navigate(`${base}/instances`) },
      };
    }

    const sections: ContextualNavSection[] = VMWARE_NAV_GROUPS.map((group) => ({
      heading: group.heading,
      items: group.items.map((entry) => {
        const href = vmwareHref(base, entry.slug);
        return {
          id: href,
          label: entry.label,
          icon: createElement(entry.icon, { size: 16 }),
          onSelect: () => navigate(href),
        };
      }),
    })).filter((s) => s.items.length > 0);

    return {
      sections,
      activeId: vmwareActiveId(base, pathname),
      back: { label: 'Back to Services', onSelect: () => navigate('/services') },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base, pathname, navigate]);

  return (
    <Routes>
      <Route index element={<VmwareDashboard />} />
      <Route path="instances" element={<InstanceList />} />
      <Route path="instances/:instanceId/*" element={<InstanceDetail />} />
      <Route path="instances/:instanceId/console" element={<InstanceConsole />} />
      <Route path="*" element={<VmwareDashboard />} />
    </Routes>
  );
}
